// A share is just an in-app post link inside the description. These helpers find
// and strip that link so it can be rendered as a rich embed ("unfurl") instead
// of raw URL text. Matches both markdown links and bare URLs to /comments/:id.

const POST_ID_RE = /\/comments\/([a-f\d]{24})\b/i;
const STRIP_RE =
  /\[[^\]]*\]\([^)]*\/comments\/[a-f\d]{24}[^)]*\)|(?:https?:\/\/[^\s)]+)?\/comments\/[a-f\d]{24}[^\s)]*/gi;

/** The first in-app post id referenced in the text, or null. */
export const extractInAppPostId = (text?: string | null): string | null => {
  if (!text) return null;
  const m = POST_ID_RE.exec(text);
  return m ? m[1].toLowerCase() : null;
};

/** The text with any in-app post links removed (they render as an embed instead). */
export const stripInAppPostLinks = (text?: string | null): string => {
  if (!text) return '';
  return text.replace(STRIP_RE, '').replace(/\n{3,}/g, '\n\n').trim();
};

/** Build the canonical in-app permalink for a post id. */
export const buildInAppPostLink = (id: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/comments/${id}`;
};

export interface ParsedBodyLinks {
  /** Body with standalone link lines removed (they render as embeds/cards). */
  text: string;
  /** In-app post ids from standalone links (render as post embeds). */
  inAppPostIds: string[];
  /** External URLs from standalone links (render as preview cards). */
  externalUrls: string[];
}

const STANDALONE_URL_RE = /^(https?:\/\/\S+)$/;

/**
 * Split a body into text + standalone links. A "standalone" link is a line that
 * is nothing but a URL — those unfurl (in-app → embed, external → preview card).
 * URLs written inline within a sentence are left in the text as normal links.
 */
export const parseBodyLinks = (raw?: string | null): ParsedBodyLinks => {
  if (!raw) return { text: '', inAppPostIds: [], externalUrls: [] };
  const inAppPostIds: string[] = [];
  const externalUrls: string[] = [];
  const kept: string[] = [];

  for (const line of raw.split('\n')) {
    const m = line.trim().match(STANDALONE_URL_RE);
    if (m) {
      const url = m[1];
      const idMatch = url.match(/\/comments\/([a-f\d]{24})\b/i);
      if (idMatch) {
        const id = idMatch[1].toLowerCase();
        if (!inAppPostIds.includes(id)) inAppPostIds.push(id);
      } else if (!externalUrls.includes(url)) {
        externalUrls.push(url);
      }
      continue;
    }
    kept.push(line);
  }

  return {
    text: kept.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    inAppPostIds,
    externalUrls,
  };
};
